import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Form, Modal, Spinner, Table } from 'react-bootstrap';
import API from '../services/api';
import './AdminVendorSecurity.css';
import { downloadVendorAgreementPdf } from '../utils/pdfDocs';

const fmtMoney = (v) => `₹ ${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('en-IN') : '—');
const emptyPaymentForm = { amount: '', mode: '', referenceNo: '' };

const buildAgreementTemplate = ({
  vendorName = 'Vendor',
  securityDepositRequired = 0,
  creditLimit = 0,
  paymentDueDays = 0,
  dailyPenaltyPercent = 0,
} = {}) => `VENDOR SUPPLY AND COMMERCIAL AGREEMENT

This Agreement is entered into between Lotus Rice Mill Pvt. Ltd. ("Company") and ${vendorName} ("Vendor") for supply of approved goods/materials as per purchase requirements issued by the Company.

1. Scope of Engagement
- Vendor shall supply only approved products, grades, packaging and quantities as accepted by the Company.
- Supply timelines, destination details and dispatch instructions issued by the Company shall be binding for each accepted order.
- Vendor shall maintain all statutory registrations, licences and business documents in valid status throughout the engagement period.

2. Security Deposit and Credit Terms
- Agreed security deposit: ${fmtMoney(securityDepositRequired)}
- Approved commercial/working limit: ${fmtMoney(creditLimit)}
- Payment due period: ${Number(paymentDueDays || 0)} days from invoice acceptance / goods receipt confirmation, unless otherwise agreed in writing.
- Delay penalty on overdue outstanding: ${Number(dailyPenaltyPercent || 0).toFixed(2)}% per day.
- The Vendor shall not raise fresh requirements, dispatches or supplies beyond the approved available limit.

3. Quality, Documentation and Compliance
- Vendor shall supply material strictly as per agreed quality specifications and applicable regulatory norms.
- Each shipment/invoice shall be supported with required documents such as GST invoice, e-way bill, transport papers, test reports and any product-specific compliance records, wherever applicable.
- In case of mismatch, shortage, quality failure or non-compliance, the Company may hold payment, reject material, seek replacement or recover losses.

4. Pricing and Payment Conditions
- Rates accepted by the Company shall be final for the applicable order unless revised in writing by authorized representatives.
- Payment shall be processed only against verified quantity, accepted quality and complete documentation.
- The Company may adjust any penalty, rejection loss, shortage, tax mismatch, damage claim or prior outstanding amount against payable invoices or deposit.

5. Operational Responsibilities
- Vendor shall ensure timely communication regarding dispatch readiness, expected arrival, stock constraints and any commercial issue affecting fulfilment.
- Vendor shall not misuse Company name, brand, customer references or confidential commercial information without written approval.
- Vendor shall comply with all applicable labour, tax, transport, food safety, packaging and legal obligations relevant to supplied goods.

6. Term, Suspension and Termination
- This Agreement remains valid until revised, suspended or terminated by the Company.
- The Company may suspend supply access, available limit or portal permissions at its sole discretion in case of non-performance, delayed payment conduct, document mismatch, quality failure, market misconduct or policy breach.
- Outstanding dues, penalty obligations and document responsibilities shall survive termination until fully resolved.

7. Dispute and Jurisdiction
- Parties shall first attempt to resolve issues through written commercial discussion.
- Failing resolution, the matter shall be subject to jurisdiction of the courts competent over the Company’s registered business location.

8. Acceptance
- By accepting this Agreement on the portal, the Vendor confirms that it has read, understood and agreed to all commercial, compliance and operational obligations mentioned above.
- This digital acceptance shall be treated as binding acknowledgement by both parties.

Special Notes
- Return, rejection, weight/quality adjustment and final settlement shall be governed by Company policy and approved transaction records.
- Any exception to this Agreement shall be valid only if recorded in writing by the Company.`;

const emptyForm = {
  securityDepositRequired: '',
  creditLimit: '',
  paymentDueDays: '',
  paymentReminderDays: '',
  dailyPenaltyPercent: '',
  agreementText: '',
  agreementUrl: '',
  adminAgreementSignature: '',
  securityDepositPaid: false,
  adminAgreementAccepted: false,
};

const resetEditor = ({
  setActiveUserId,
  setActiveVendorName,
  setActiveVendorRow,
  setForm,
  setPaymentForm,
  setPaymentRows,
  setPaymentSummary,
  setPaymentError,
}) => {
  setActiveUserId('');
  setActiveVendorName('');
  setActiveVendorRow(null);
  setForm(emptyForm);
  setPaymentForm(emptyPaymentForm);
  setPaymentRows([]);
  setPaymentSummary({ total: 0, totalAmount: 0 });
  setPaymentError('');
};

const AdminVendorSecurity = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeUserId, setActiveUserId] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [activeVendorName, setActiveVendorName] = useState('');
  const [activeVendorRow, setActiveVendorRow] = useState(null);
  const [paymentForm, setPaymentForm] = useState(emptyPaymentForm);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [reminderSending, setReminderSending] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentRows, setPaymentRows] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState({ total: 0, totalAmount: 0 });
  const [paymentError, setPaymentError] = useState('');

  const closeEditor = () =>
    resetEditor({
      setActiveUserId,
      setActiveVendorName,
      setActiveVendorRow,
      setForm,
      setPaymentForm,
      setPaymentRows,
      setPaymentSummary,
      setPaymentError,
    });

  const loadRows = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await API.get('/admin/vendor-security');
      const list = Array.isArray(res.data) ? res.data : [];
      setRows(list);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load vendor commercial settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
  }, []);

  const filteredRows = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const blob = [r.vendorName, r.email, r.mobile].filter(Boolean).join(' ').toLowerCase();
      return blob.includes(q);
    });
  }, [rows, query]);

  const startEdit = (row) => {
    setActiveVendorRow(row);
    setActiveUserId(String(row.userId || ''));
    setActiveVendorName(row.vendorName || 'Vendor');
    setMessage('');
    setError('');
    setForm({
      securityDepositRequired: String(row.securityDepositRequired ?? ''),
      creditLimit: String(row.creditLimit ?? ''),
      paymentDueDays: String(row.paymentDueDays ?? ''),
      paymentReminderDays: String(row.paymentReminderDays ?? ''),
      dailyPenaltyPercent: String(row.dailyPenaltyPercent ?? ''),
      agreementText: row.agreementText || '',
      agreementUrl: row.agreementUrl || '',
      adminAgreementSignature: row.agreementAcceptedByAdminSignature || '',
      securityDepositPaid: !!row.securityDepositPaid,
      adminAgreementAccepted: !!row.agreementAcceptedByAdmin,
    });
    setPaymentForm(emptyPaymentForm);
    setPaymentRows([]);
    setPaymentSummary({ total: 0, totalAmount: 0 });
    setPaymentError('');
    if (row.userId) {
      loadVendorPayments(row.userId);
    }
  };

  const onChange = (e) => {
    const { name, type, value, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const onPaymentChange = (e) => {
    const { name, value } = e.target;
    setPaymentForm((prev) => ({ ...prev, [name]: value }));
  };

  const loadVendorPayments = async (userId = activeUserId) => {
    if (!userId) return;
    setPaymentLoading(true);
    setPaymentError('');
    try {
      const res = await API.get(`/admin/vendors/${userId}/security-payments`);
      const nextRows = Array.isArray(res?.data?.rows) ? res.data.rows : [];
      setPaymentRows(nextRows);
      setPaymentSummary({
        total: Number(res?.data?.summary?.total || nextRows.length || 0),
        totalAmount: Number(
          res?.data?.summary?.totalAmount ||
            nextRows.reduce((sum, r) => sum + Number(r?.amount || 0), 0)
        ),
      });
    } catch (e) {
      setPaymentError(e?.response?.data?.message || 'Failed to load payment updates');
    } finally {
      setPaymentLoading(false);
    }
  };

  const addPaymentUpdate = async (e) => {
    e.preventDefault();
    if (!activeUserId || paymentSaving) return;
    const amount = Number(paymentForm.amount || 0);
    const mode = String(paymentForm.mode || '').trim();
    if (!Number.isFinite(amount) || amount <= 0) {
      setPaymentError('Valid payment amount is required');
      return;
    }
    if (!mode) {
      setPaymentError('Payment mode is required');
      return;
    }
    setPaymentSaving(true);
    setPaymentError('');
    try {
      await API.post(`/admin/vendors/${activeUserId}/security-payments`, {
        amount,
        mode,
        referenceNo: paymentForm.referenceNo,
      });
      setPaymentForm(emptyPaymentForm);
      setMessage('Payment update added and ledger credit posted.');
      await Promise.all([loadRows(), loadVendorPayments(activeUserId)]);
    } catch (e2) {
      setPaymentError(e2?.response?.data?.message || 'Failed to add payment update');
    } finally {
      setPaymentSaving(false);
    }
  };

  const applyRecommendedTemplate = () => {
    setForm((prev) => ({
      ...prev,
      agreementText: buildAgreementTemplate({
        vendorName: activeVendorName || 'Vendor',
        securityDepositRequired: prev.securityDepositRequired,
        creditLimit: prev.creditLimit,
        paymentDueDays: prev.paymentDueDays,
        dailyPenaltyPercent: prev.dailyPenaltyPercent,
      }),
    }));
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!activeUserId) return;
    setSaving(true);
    setMessage('');
    setError('');
    try {
      await API.put(`/admin/vendors/${activeUserId}/security-config`, {
        securityDepositRequired: Number(form.securityDepositRequired || 0),
        creditLimit: Number(form.creditLimit || 0),
        paymentDueDays: Number(form.paymentDueDays || 0),
        paymentReminderDays: Number(form.paymentReminderDays || 0),
        dailyPenaltyPercent: Number(form.dailyPenaltyPercent || 0),
        agreementText: form.agreementText,
        agreementUrl: form.agreementUrl,
        adminAgreementSignature: form.adminAgreementSignature,
        securityDepositPaid: !!form.securityDepositPaid,
        adminAgreementAccepted: !!form.adminAgreementAccepted,
      });
      setMessage('Vendor commercial settings updated.');
      await loadRows();
    } catch (e2) {
      setError(e2?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const sendPaymentReminder = async () => {
    if (!activeUserId || reminderSending) return;
    setReminderSending(true);
    setMessage('');
    setError('');
    try {
      const res = await API.post(`/admin/vendors/${activeUserId}/payment-reminder`);
      setMessage(res?.data?.message || 'Vendor payment reminder sent.');
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to send vendor payment reminder');
    } finally {
      setReminderSending(false);
    }
  };

  return (
    <div className="admin-vs-page">
      <div className="admin-vs-header">
        <div>
          <h1 className="admin-vs-title">Vendor Security & Credit Control</h1>
          <p className="admin-vs-subtitle">Manage financial thresholds, security deposits, and legal agreements</p>
        </div>
        <div className="admin-vs-header-actions">
          <div className="admin-vs-search-wrap">
            <span className="material-symbols-outlined">search</span>
            <Form.Control
              className="admin-vs-search"
              placeholder="Search vendor name, email..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <Button className="admin-vs-refresh-btn" type="button" onClick={loadRows}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {message ? <Alert variant="success">{message}</Alert> : null}
      {error ? <Alert variant="danger">{error}</Alert> : null}

      <Card className="admin-vs-card">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table hover className="align-middle admin-vs-table mb-0">
              <thead>
                <tr>
                  <th>Vendor Details</th>
                  <th>Security Deposit</th>
                  <th>Status</th>
                  <th>Credit Limit</th>
                  <th>Used / Avail.</th>
                  <th className="text-center">Due / Delay</th>
                  <th>Agreement</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center py-4">
                      <Spinner size="sm" animation="border" /> Loading...
                    </td>
                  </tr>
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center text-muted py-4">No vendors found.</td>
                  </tr>
                ) : (
                  filteredRows.map((r) => {
                    const ratio = Number(r.creditLimit || 0)
                      ? Math.max(0, Math.min(100, (Number(r.outstandingWithPenalty || 0) / Number(r.creditLimit || 1)) * 100))
                      : 0;

                    return (
                      <tr key={String(r.vendorId)}>
                        <td>
                          <div className="admin-vs-vendor-name">{r.vendorName || 'Vendor'}</div>
                          <div className="admin-vs-vendor-meta">{r.mobile || '—'}</div>
                          <div className="admin-vs-vendor-sub">{r.email || '—'}</div>
                        </td>
                        <td className="fw-semibold">{fmtMoney(r.securityDepositRequired)}</td>
                        <td>
                          <span className={r.securityDepositPaid ? 'admin-vs-pill paid' : 'admin-vs-pill pending'}>
                            {r.securityDepositPaid ? 'Paid Full' : 'Pending'}
                          </span>
                        </td>
                        <td className="fw-semibold">{fmtMoney(r.creditLimit)}</td>
                        <td>
                          <div className="admin-vs-usage">
                            <div className="admin-vs-usage-bar">
                              <div className="admin-vs-usage-fill" style={{ width: `${ratio}%` }} />
                            </div>
                            <div className="admin-vs-usage-text">
                              {fmtMoney(r.outstandingWithPenalty)} / {fmtMoney(r.availableLimit)}
                            </div>
                          </div>
                        </td>
                        <td className="text-center">
                          <div className="fw-semibold">{r.paymentDueDays || 0} Days</div>
                          <div className="admin-vs-penalty">Reminder: {r.paymentReminderDays || 0} days</div>
                          <div className="admin-vs-penalty">{Number(r.dailyPenaltyPercent || 0).toFixed(2)}% / day</div>
                        </td>
                        <td>
                          <div className="admin-vs-agreement-status">
                            <span className={`admin-vs-dot ${r.agreementAcceptedByAdmin ? 'active' : ''}`} />
                            {r.agreementAcceptedByAdmin ? 'Admin Accepted' : 'Draft'}
                          </div>
                        </td>
                        <td className="text-end">
                          <Button size="sm" className="admin-vs-configure-btn" onClick={() => startEdit(r)}>
                            Configure
                          </Button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      <Modal show={!!activeUserId} onHide={closeEditor} centered size="xl" dialogClassName="admin-vs-modal">
        <Modal.Header closeButton className="admin-vs-modal-header">
          <div>
            <Modal.Title>Configure Vendor: {activeVendorName || 'Vendor'}</Modal.Title>
            <div className="admin-vs-modal-subtitle">Establish credit limits, penalties, and legal terms</div>
          </div>
        </Modal.Header>
        <Modal.Body className="admin-vs-modal-body">
          <Form onSubmit={onSave}>
            <section>
              <div className="admin-vs-section-title">
                <div className="admin-vs-section-icon">
                  <span className="material-symbols-outlined">currency_rupee_circle</span>
                </div>
                <h2>Commercial Terms</h2>
              </div>
              <div className="admin-vs-grid">
                <Form.Group>
                  <Form.Label>Security Deposit (₹)</Form.Label>
                  <Form.Control
                    name="securityDepositRequired"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.securityDepositRequired}
                    onChange={onChange}
                    required
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label>Credit Limit (₹)</Form.Label>
                  <Form.Control
                    name="creditLimit"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.creditLimit}
                    onChange={onChange}
                    required
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label>Payment Due Days</Form.Label>
                  <Form.Control
                    name="paymentDueDays"
                    type="number"
                    min="0"
                    step="1"
                    value={form.paymentDueDays}
                    onChange={onChange}
                    required
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label>Payment Reminder Days</Form.Label>
                  <Form.Control
                    name="paymentReminderDays"
                    type="number"
                    min="0"
                    step="1"
                    value={form.paymentReminderDays}
                    onChange={onChange}
                    required
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label>Delay Penalty (% / day)</Form.Label>
                  <Form.Control
                    name="dailyPenaltyPercent"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={form.dailyPenaltyPercent}
                    onChange={onChange}
                    required
                  />
                </Form.Group>
              </div>
            </section>

            <section className="admin-vs-section admin-vs-section-muted">
              <div className="admin-vs-section-title">
                <div className="admin-vs-section-icon">
                  <span className="material-symbols-outlined">description</span>
                </div>
                <h2>Agreement & Compliance</h2>
              </div>
              <div className="admin-vs-agreement-head">
                <div className="admin-vs-mini-label">Legal Terms & Conditions</div>
                <Button variant="link" className="admin-vs-inline-link" size="sm" type="button" onClick={applyRecommendedTemplate}>
                  <span className="material-symbols-outlined">autorenew</span>
                  Use Recommended Format
                </Button>
              </div>
              <Form.Control
                as="textarea"
                rows={5}
                name="agreementText"
                value={form.agreementText}
                onChange={onChange}
                placeholder="Enter full legal agreement text here..."
              />
              <div className="admin-vs-grid mt-3">
                <Form.Group>
                  <Form.Label>Agreement Document URL</Form.Label>
                  <Form.Control
                    name="agreementUrl"
                    value={form.agreementUrl}
                    onChange={onChange}
                    placeholder="https://docs.google.com/document/d/..."
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Admin Signature Image URL</Form.Label>
                  <Form.Control
                    name="adminAgreementSignature"
                    value={form.adminAgreementSignature}
                    onChange={onChange}
                    placeholder="https://cdn.lotusricemill.com/sig/admin-01.png"
                  />
                </Form.Group>
              </div>
              <div className="small text-muted mt-2">
                Agar abhi empty chhodo to agreement me admin name text hi show hoga.
              </div>
              <div className="admin-vs-toggle-row">
                <Form.Check
                  type="switch"
                  id="securityDepositPaid"
                  name="securityDepositPaid"
                  checked={!!form.securityDepositPaid}
                  onChange={onChange}
                  label="Security deposit paid"
                />
                <Form.Check
                  type="switch"
                  id="adminAgreementAccepted"
                  name="adminAgreementAccepted"
                  checked={!!form.adminAgreementAccepted}
                  onChange={onChange}
                  label="Admin accepts agreement"
                />
              </div>
              {activeVendorRow ? (
                <div className="mt-2 small text-muted">
                  Vendor acceptance: {activeVendorRow.agreementAcceptedByVendor ? 'Accepted' : 'Pending'}
                  {activeVendorRow.agreementAcceptedByVendorAt ? ` (${new Date(activeVendorRow.agreementAcceptedByVendorAt).toLocaleString('en-IN')})` : ''}
                </div>
              ) : null}
            </section>

            <section className="admin-vs-section">
              <div className="admin-vs-section-title">
                <div className="admin-vs-section-icon">
                  <span className="material-symbols-outlined">history</span>
                </div>
                <h2>Payment Update History</h2>
              </div>
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div />
                <div className="small text-muted">
                  Total Entries: {paymentSummary.total || 0} | Total Amount: {fmtMoney(paymentSummary.totalAmount || 0)}
                </div>
              </div>
              {paymentError ? <Alert variant="danger" className="py-2">{paymentError}</Alert> : null}
              <div className="admin-vs-payment-entry">
                <div>
                  <div className="admin-vs-mini-label">Amount (₹)</div>
                  <Form.Control
                    name="amount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={paymentForm.amount}
                    onChange={onPaymentChange}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <div className="admin-vs-mini-label">Mode</div>
                  <Form.Select name="mode" value={paymentForm.mode} onChange={onPaymentChange}>
                    <option value="">NEFT / IMPS</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="NEFT">NEFT</option>
                    <option value="UPI">UPI</option>
                    <option value="Cash">Cash</option>
                    <option value="Cheque">Cheque</option>
                  </Form.Select>
                </div>
                <div className="admin-vs-payment-action-wrap">
                  <div className="admin-vs-payment-action">
                    <div className="admin-vs-payment-ref">
                      <div className="admin-vs-mini-label">Reference No</div>
                      <Form.Control
                        name="referenceNo"
                        value={paymentForm.referenceNo}
                        onChange={onPaymentChange}
                        placeholder="TXN123456789"
                      />
                    </div>
                    <Button type="button" className="admin-vs-add-btn" onClick={addPaymentUpdate} disabled={paymentSaving || !activeUserId}>
                      {paymentSaving ? 'Updating...' : 'Add Entry'}
                    </Button>
                  </div>
                </div>
              </div>
              <div className="mt-2 d-flex gap-2">
                <Button type="button" variant="outline-secondary" onClick={() => loadVendorPayments(activeUserId)} disabled={paymentLoading || !activeUserId}>
                  {paymentLoading ? 'Refreshing...' : 'Refresh History'}
                </Button>
                <Button type="button" variant="outline-warning" onClick={sendPaymentReminder} disabled={reminderSending || !activeUserId}>
                  {reminderSending ? 'Sending...' : 'Send Reminder'}
                </Button>
              </div>
              <div className="table-responsive mt-3 admin-vs-payment-table">
                <Table hover size="sm" className="align-middle mb-0 admin-vs-history-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Mode</th>
                      <th>Reference No</th>
                      <th>Status</th>
                      <th>Updated By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentLoading ? (
                      <tr>
                        <td colSpan="6" className="text-center py-3">
                          <Spinner size="sm" animation="border" /> Loading payment history...
                        </td>
                      </tr>
                    ) : paymentRows.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center text-muted py-3">No payment updates found.</td>
                      </tr>
                    ) : (
                      paymentRows.map((r) => (
                        <tr key={String(r._id)}>
                          <td>{fmtDateTime(r.createdAt)}</td>
                          <td>{fmtMoney(r.amount)}</td>
                          <td>{r.mode || '—'}</td>
                          <td>{r.referenceNo || '—'}</td>
                          <td className="text-capitalize">{r.status || '—'}</td>
                          <td>{r.createdBy?.fullName || '—'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </section>

            <div className="admin-vs-modal-footer">
              <Button variant="link" className="admin-vs-cancel-btn" onClick={closeEditor}>
                Cancel
              </Button>
              <div className="d-flex gap-2">
                {activeVendorRow ? (
                  <Button
                    variant="outline-secondary"
                    onClick={() =>
                      downloadVendorAgreementPdf({
                        vendor: activeVendorRow,
                        financialSummary: {
                          securityDepositPaid: activeVendorRow.securityDepositPaid,
                          securityDepositRequired: activeVendorRow.securityDepositRequired,
                          creditLimit: activeVendorRow.creditLimit,
                          availableLimit: activeVendorRow.availableLimit,
                          paymentDueDays: activeVendorRow.paymentDueDays,
                          dailyPenaltyPercent: activeVendorRow.dailyPenaltyPercent,
                        },
                      })
                    }
                  >
                    PDF
                  </Button>
                ) : null}
                <Button type="submit" className="admin-vs-save-btn" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default AdminVendorSecurity;
